import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { QuestionsService } from '@APP/services/questions-service';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators';
import { commonPopupMobileStyle, commonPopupStyle } from '../../styles/common-style';
import { t } from '../../utils/locale';
import { DeviceAwareComponent } from '../components/device-aware-component';
import { PelicanCircle, redWarningIcon } from '../icons/icons';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import { HTMLTemplateResult } from 'lit-html';

type OwnDeps = {
  questionsService: QuestionsService;
};

@requires(QuestionsService.serviceName)
@customElement('non-eligible-popup')
export class NonEligiblePopup extends DepInjMixin<OwnDeps>(DeviceAwareComponent) {
  static get baseStyles() {
    return [
      commonPopupStyle,
      css`
        .title {
          font-weight: 700;
          font-size: 4rem;
        margin-bottom: 1.75rem;
      }

      .sub-title {
        font-size: 4rem;
        font-weight: 700;
        margin-bottom: 2rem;
        line-height: 4rem;
        color: var(--c-text-sub);
      }

      .message {
        font-weight: 400;
        max-width: var(--message-max-width, 39rem);
        color: var(--c-text);
      }

      svg, img {
        max-width: 26.5rem;
        max-height: 36.5rem;
      }

      ul {
        max-width: 60rem;
        list-style: none;   /* removes default bullets */
        padding-left: 0;    /* removes left indent */
        margin: 0;
      }

      ul li {
        font-size: 2rem;
        margin-block: 0.8rem;
      }

      .bullet-icon svg {
        height: 2rem;
        width: 3rem;
        margin-inline-end: 1rem;
      }

    `,
    ];
  };

  static get mobileStyles() {
    return [commonPopupMobileStyle];
  }

  @property({ type: String })
    value: string = '';

  @property({ type: String, attribute:'technical-data' })
    technicalData: string = '';

  @property({ type: String, attribute:'header-text' })
    headerText: string = '';

  renderMobileLayout(title: string, messages: HTMLTemplateResult) {
    return html`
      <div class="">
        <div class="popup-image-container">
          ${PelicanCircle}
        </div>
        <div class="col-1">
          <h2 class="title">${title}</h2>
        </div>
        <div class="col-3">
          ${messages}
        </div>
      </div>
    `;
  }

  renderDesktopLayout(title: string, messages: HTMLTemplateResult) {
    return html`
      <div class="grid-container">
        <div class="col-1"></div>
        <div class="col-2">
          <h1 class="title">${title}</h1>
          ${messages}
        </div>
      </div>
    `;
  }

  renderContent() {
    const isRatingError = this.value.startsWith('Rate');
    const title = isRatingError ? t('Something went wrong') : t('E_Non_Eligible_Sub');
    let safeValue = this.value;
    const dontReplaceQuotes = this.technicalData?.includes('no-changing-quotes');
    if (!dontReplaceQuotes) safeValue = this.value.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const isBullets = this.technicalData?.includes('specify-errors-as-bullets') || safeValue.includes('$_BULLET');
    let messages = isRatingError
      ? html`<h2 class="message">${unsafeHTML(t('E_Rating_1', this.deps.questionsService.model.getSupportPhoneNumberText()))}</h2>`
      : html`<h2 class="message">${unsafeHTML(safeValue)}</h2>`;
    if (isBullets) {
      // Split on "$_BULLET". make sure each bullet starts with a capital letter, that there are no spaces
      // on the bullet beginning or end, and that each bullet ends with 1 dot
      const bulletPoints = safeValue.split('$_BULLET').map(point => point.trim()).filter(Boolean);
      // formatting
      bulletPoints.forEach((point, index) => {
        // Capitalize the first letter
        bulletPoints[index] = point.charAt(0).toUpperCase() + point.slice(1);
        // Remove dots at the beginning
        bulletPoints[index] = bulletPoints[index].replace(/^\.+/, '');
        // Remove spaces at the beginning and end
        bulletPoints[index] = bulletPoints[index].replace(/^\s+|\s+$/g, '');
        // Ensure it ends with a dot
        if (!bulletPoints[index].endsWith('.')) {
          bulletPoints[index] += '.';
        }
        // make sure there are no 2 dots at the end
        if (bulletPoints[index].endsWith('..')) {
          bulletPoints[index] = bulletPoints[index].slice(0, -1);
        }
      });
      messages = html`
      <div>
        <h2 class="message">${t(this.headerText)}</h2>
        <ul>
          ${bulletPoints.map(point => html`<li><span class="bullet-icon">${redWarningIcon}</span>${unsafeHTML(point)}</li>`)}
        </ul>
      </div>`;
    }

    return this.userDevice === 'desktop' ? this.renderDesktopLayout(title, messages) : this.renderMobileLayout(title, messages);
  }
}